import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Shield, 
  Smartphone, 
  Database, 
  GitBranch, 
  Server,
  Bot,
  Layers
} from 'lucide-react';
import './Expertise.css';

const Expertise = () => {
  const expertiseAreas = [
    {
      category: 'Fintech',
      icon: <Zap size={24} />,
      technologies: [
        'Payment Gateways',
        'Blockchain',
        'Digital Wallets',
        'Risk Management',
        'Regulatory Compliance',
        'Open Banking APIs'
      ],
      color: '#10B981'
    },
    {
      category: 'ERP Systems',
      icon: <Database size={24} />,
      technologies: [
        'SAP Implementation',
        'Oracle ERP',
        'Microsoft Dynamics',
        'Custom ERP Solutions',
        'Business Intelligence',
        'Workflow Automation'
      ],
      color: '#3B82F6'
    },
    {
      category: 'Supply Chain',
      icon: <Layers size={24} />,
      technologies: [
        'Inventory Management',
        'Logistics Optimization',
        'Vendor Management',
        'IoT Integration',
        'Predictive Analytics',
        'Real-time Tracking'
      ],
      color: '#8B5CF6'
    },
    {
      category: 'SDLC',
      icon: <GitBranch size={24} />,
      technologies: [
        'Agile & Scrum',
        'DevOps & CI/CD',
        'Quality Assurance',
        'Project Management',
        'Code Review',
        'Testing Automation'
      ],
      color: '#F59E0B'
    },
    {
      category: 'Cloud Services',
      icon: <Server size={24} />,
      technologies: [
        'AWS Solutions',
        'Microsoft Azure',
        'Google Cloud',
        'Cloud Migration',
        'Serverless Architecture',
        'Container Orchestration'
      ],
      color: '#EF4444'
    },
    {
      category: 'AI & ML',
      icon: <Bot size={24} />,
      technologies: [
        'Machine Learning',
        'Natural Language Processing',
        'Computer Vision',
        'Predictive Analytics',
        'Chatbots & Virtual Assistants',
        'Data Science'
      ],
      color: '#06B6D4'
    }
  ];

  return (
    <section id="expertise" className="expertise section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Expertise</h2>
          <p className="section-subtitle">
            Deep technical knowledge across multiple domains and cutting-edge technologies
          </p>
        </motion.div>

        <div className="expertise-grid">
          {expertiseAreas.map((area, index) => (
            <motion.div
              key={index}
              className="expertise-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <div 
                className="expertise-header"
                style={{ backgroundColor: `${area.color}15` }}
              >
                <div 
                  className="expertise-icon"
                  style={{ color: area.color }}
                >
                  {area.icon}
                </div>
                <h3 className="expertise-title">{area.category}</h3>
              </div>
              
              <div className="expertise-technologies">
                {area.technologies.map((tech, techIndex) => (
                  <motion.span
                    key={techIndex}
                    className="tech-badge"
                    style={{ borderColor: area.color, color: area.color }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: (index * 0.1) + (techIndex * 0.05) }}
                    viewport={{ once: true }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="expertise-summary"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="summary-content">
            <div className="summary-text">
              <h3>Why Our Expertise Matters</h3>
              <p>
                Our multidisciplinary approach ensures that we can handle complex, cross-functional 
                projects that require deep understanding of multiple technologies and business domains. 
                We stay ahead of industry trends and continuously update our skills to provide you with 
                the most innovative and effective solutions.
              </p>
            </div>
            
            <div className="summary-stats">
              <div className="stat-item">
                <Smartphone size={24} />
                <span>Mobile-First Approach</span>
              </div>
              <div className="stat-item">
                <Shield size={24} />
                <span>Security-Focused Development</span>
              </div>
              <div className="stat-item">
                <Zap size={24} />
                <span>Performance Optimized</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Expertise;
